import { Card, Dropdown, Icon, css } from '@appd/uik';
import { getActionsRegistry } from '..';
import { get } from 'lodash';
import React, { useState } from 'react';
import { emitter, getGlobals } from './DashboardViewer/globals';
import { useRouter } from 'next/router';
import { MAGNETIC_TOKENS } from '@appd/uik';

interface CardProps {
  id: string;
  title: string;
  subTitle: string;
  accentColor?: string;
  healthStatus?: number;
  selected: boolean;
  onClick: (event: React.MouseEvent<HTMLDivElement>) => void;
  titleTag: {
    text?: string;
    type?: 'DEFAULT' | 'INACTIVE' | 'NEGATIVE' | 'POSITIVE' | 'TEXT_EMPHASIS_ONLY' | 'TEXT_ONLY' | 'WARNING';
  };
  children: React.ReactNode;
}

const {
  COLORS: { WARNING_BG_MEDIUM },
  RADIUS,
  SPACING,
  TYPOGRAPHY: { P4_EMPHASIS },
} = MAGNETIC_TOKENS;

const getHealthIcons = (status: number) => {
  const HEALTHICONS: { [key: number]: JSX.Element } = {
    0: <Icon.Health.Normal width={20} />,
    1: <Icon.Status.Unknown width={20} />,
    2: <Icon.Health.Warning width={20} />,
    3: <Icon.Health.Critical width={20} />,
  };
  return HEALTHICONS[status] ?? <Icon.Status.Unknown width={14} />;
};

const ICON_BACKGROUND = css({
  alignItems: 'center',
  backgroundColor: WARNING_BG_MEDIUM,
  borderRadius: RADIUS.DEFAULT,
  display: 'flex',
  height: 25,
  justifyContent: 'center',
  width: 25,
});

export const DashifyCard = ({
  id,
  title = '',
  subTitle = '',
  titleTag,
  accentColor,
  healthStatus,
  onClick,
  selected = false,
  children,
  ...rest
}: CardProps) => {
  const router = useRouter();
  const ActionsRegistry = getActionsRegistry(router);
  const containerActions = ['edit'];
  const actions = Object.keys(ActionsRegistry).filter(k => containerActions.indexOf(k) !== -1);
  const globals = getGlobals();
  const [editMode, setEditMode] = useState(get(globals, 'editMode', false) as boolean);
  const borderColor = selected ? 'var(--control-border-active)' : '';

  emitter &&
    emitter.on('globalsChanged', e => {
      const { path, data } = e as any;
      if (path === '/globals/editMode' && editMode !== data) {
        setEditMode(data);
      }
    });

  return (
    <Card
      className={{ height: '100%', boxSizing: 'border-box', borderColor: borderColor }}
      title={title}
      subtitle={subTitle}
      titleTag={titleTag}
      accentColor={accentColor}
      onClick={onClick}
      right={healthStatus !== undefined && <div>{getHealthIcons(healthStatus)}</div>}
    >
      <div
        className={css({
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'space-between',
          position: 'absolute',
          top: '6px',
          right: '4px',
          zIndex: 5000,
        })}
        // Prevent the drag and drop interaction from triggering on `<Layout>`, so the menu can be opened.
        onPointerDown={event => event.preventDefault()}
      >
        {editMode && (
          <Dropdown
            triggerClassName={css({ pointerEvents: 'initial', '--i': 'NaN' })}
            icon={<Icon.Navigation.MoreHorizontal />}
            kind="TERTIARY"
            onClick={selectedIndex => {}}
            onSelect={selected => {
              const action = actions?.[selected[0]];
              ActionsRegistry[action].onClick(id);
            }}
            contentClassName={css({ width: 50 })}
          >
            {actions.map(actionKey => (
              <Dropdown.Option key={ActionsRegistry[actionKey].label}>
                {ActionsRegistry[actionKey].label}
              </Dropdown.Option>
            ))}
          </Dropdown>
        )}
      </div>

      {children}
    </Card>
  );
};
